/* Daily-activity calendar — a week grid over the daily ledger (by daily number). */

import { dateForDay, dayNumber } from '../engine/seed';
import type { DailyLedger } from '../state/statsLogic';

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

export function dateKey(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export interface CalendarDay {
  key: string;
  /** Day of the month, for display. */
  date: number;
  /** The daily number (#1 = the first ClueVine day). */
  dayNum: number;
  /** Dailies won / finished that day (archive plays included). */
  won: number;
  played: number;
  isToday: boolean;
  future: boolean;
  /** Before ClueVine's first daily — nothing to play. */
  beforeStart: boolean;
}

/**
 * Build a `weeks` × 7 grid ending on the week containing `now` (shifted back by
 * `page` screens of `weeks`). `now` is injectable for testing.
 */
export function buildCalendar(
  ledger: DailyLedger,
  weeks = 5,
  now: Date = new Date(),
  page = 0,
): CalendarDay[][] {
  const today = dayNumber(now);
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = new Date(todayDate);
  start.setDate(start.getDate() - start.getDay() - (weeks - 1) * 7 - page * weeks * 7);

  const grid: CalendarDay[][] = [];
  for (let w = 0; w < weeks; w++) {
    const row: CalendarDay[] = [];
    for (let d = 0; d < 7; d++) {
      const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + w * 7 + d);
      const dayNum = dayNumber(day);
      const recs = Object.values(ledger[String(dayNum)] ?? {});
      row.push({
        key: dateKey(day.getTime()),
        date: day.getDate(),
        dayNum,
        won: recs.filter((r) => r.status === 'won').length,
        played: recs.length,
        isToday: dayNum === today,
        future: dayNum > today,
        beforeStart: dayNum < 1,
      });
    }
    grid.push(row);
  }
  return grid;
}

/** How many calendar pages back reach the first ClueVine day. */
export function maxCalendarPage(weeks = 5, now: Date = new Date()): number {
  const first = dateForDay(1);
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((Date.UTC(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate()) -
    Date.UTC(first.getFullYear(), first.getMonth(), first.getDate())) / 86_400_000);
  return Math.max(0, Math.floor((days + todayDate.getDay()) / (weeks * 7)));
}
